import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Engagement } from "@eagledispatch/shared";
import { apiFetch } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import Button from "../../components/Button";

/**
 * EngagementMap — SPEC-42 §2.4 Office Map Surface.
 *
 * Uses plain Leaflet with OSM tiles and custom L.divIcon badge markers.
 * - Live: blue pins at lat/lng (skips nulls). Badge indicates count of open engagements per property.
 * - History: faded gray pins for closed engagements (toggleable, default off).
 * - Popup: Property site name / Job number / Status / Opened date /
 *   (Removed date if closed) / Driver name / (GPS Accuracy if available).
 * - Polls on window focus.
 */
export default function EngagementMap() {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);

  const [showHistory, setShowHistory] = useState(false);
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEngagements = (includeHistory: boolean) => {
    setLoading(true);
    setError(null);
    const stateParam = includeHistory ? "all" : "open";
    apiFetch<{ engagements: Engagement[] }>(`/api/v1/engagements?state=${stateParam}`)
      .then((res) => {
        setEngagements(res.engagements);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  };

  // Initial load + reload when showHistory changes
  useEffect(() => {
    loadEngagements(showHistory);
  }, [showHistory]);

  // Refresh on window focus (active-use model, no interval)
  useEffect(() => {
    const handleFocus = () => {
      loadEngagements(showHistory);
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [showHistory]);

  // Initialize Leaflet Map once
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapInstanceRef.current) return;

    // Default center: Salinas Valley / Monterey County coords (36.6777, -121.6555), zoom 11
    const map = L.map(mapContainerRef.current).setView([36.6777, -121.6555], 11);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    const layerGroup = L.layerGroup().addTo(map);
    markersLayerRef.current = layerGroup;
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  // Update Markers when engagements change
  useEffect(() => {
    const layer = markersLayerRef.current;
    const map = mapInstanceRef.current;
    if (!layer || !map) return;

    layer.clearLayers();

    // Group open engagements by propertyId to calculate badge counts
    const openCountByProperty = new Map<string, number>();
    for (const eng of engagements) {
      if (eng.closedAt === null) {
        const current = openCountByProperty.get(eng.propertyId) || 0;
        openCountByProperty.set(eng.propertyId, current + 1);
      }
    }

    const validLatLngs: L.LatLngExpression[] = [];

    for (const eng of engagements) {
      if (eng.lat === null || eng.lng === null) continue;

      const isOpen = eng.closedAt === null;
      const openCount = openCountByProperty.get(eng.propertyId) || 1;

      // Custom divIcon badge marker
      const isHistory = !isOpen;
      const markerClass = isHistory ? "pin-marker pin-closed" : "pin-marker pin-open";
      const badgeHtml =
        !isHistory && openCount > 1
          ? `<div class="pin-badge">${openCount}</div>`
          : "";

      const icon = L.divIcon({
        className: markerClass,
        html: `<div class="pin-icon-inner">${badgeHtml}</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
      });

      const popupEl = document.createElement("div");
      popupEl.className = "engagement-popup";

      const titleEl = document.createElement("h4");
      titleEl.textContent = eng.propertySiteName || "Unknown Property";
      popupEl.appendChild(titleEl);

      const pJob = document.createElement("p");
      const strongJob = document.createElement("strong");
      strongJob.textContent = "Job: ";
      pJob.appendChild(strongJob);
      pJob.appendChild(document.createTextNode(eng.jobNumber || "—"));
      popupEl.appendChild(pJob);

      const pStatus = document.createElement("p");
      const strongStatus = document.createElement("strong");
      strongStatus.textContent = "Status: ";
      pStatus.appendChild(strongStatus);
      const spanStatus = document.createElement("span");
      if (isOpen) {
        spanStatus.style.color = "#2E6E77";
        spanStatus.style.fontWeight = "bold";
        spanStatus.textContent = "OPEN";
      } else {
        spanStatus.style.color = "#5E7278";
        spanStatus.textContent = "CLOSED";
      }
      pStatus.appendChild(spanStatus);
      popupEl.appendChild(pStatus);

      const pPlaced = document.createElement("p");
      const strongPlaced = document.createElement("strong");
      strongPlaced.textContent = "Placed: ";
      pPlaced.appendChild(strongPlaced);
      pPlaced.appendChild(document.createTextNode(formatDateTime(eng.openedAt)));
      popupEl.appendChild(pPlaced);

      if (eng.closedAt) {
        const pRemoved = document.createElement("p");
        const strongRemoved = document.createElement("strong");
        strongRemoved.textContent = "Removed: ";
        pRemoved.appendChild(strongRemoved);
        pRemoved.appendChild(document.createTextNode(formatDateTime(eng.closedAt)));
        popupEl.appendChild(pRemoved);
      }

      const pDriver = document.createElement("p");
      const strongDriver = document.createElement("strong");
      strongDriver.textContent = "Driver: ";
      pDriver.appendChild(strongDriver);
      pDriver.appendChild(document.createTextNode(eng.driverName || "—"));
      popupEl.appendChild(pDriver);

      if (eng.accuracyM !== null) {
        const pAcc = document.createElement("p");
        const strongAcc = document.createElement("strong");
        strongAcc.textContent = "GPS Accuracy: ";
        pAcc.appendChild(strongAcc);
        pAcc.appendChild(document.createTextNode(`±${eng.accuracyM}m`));
        popupEl.appendChild(pAcc);
      }

      const marker = L.marker([eng.lat, eng.lng], { icon }).bindPopup(popupEl);
      layer.addLayer(marker);
      validLatLngs.push([eng.lat, eng.lng]);
    }

    // Auto-fit bounds if we have valid coordinates
    if (validLatLngs.length > 0) {
      const bounds = L.latLngBounds(validLatLngs);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [engagements]);

  return (
    <div className="engagement-map-view">
      <div className="engagement-map-controls">
        <div className="engagement-map-title">
          <h2>Active Dumpster Engagements</h2>
          <span className="muted" style={{ fontSize: "13px" }}>
            {loading ? "Loading map data…" : `${engagements.filter((e) => !e.closedAt).length} active placement(s)`}
          </span>
        </div>

        <div className="engagement-map-actions">
          <label className="engagement-history-toggle">
            <input
              type="checkbox"
              checked={showHistory}
              onChange={(e) => setShowHistory(e.target.checked)}
            />
            <span>Show Faded History (Closed)</span>
          </label>

          <Button variant="ghost" onClick={() => loadEngagements(showHistory)}>
            ↻ Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="banner banner-danger" style={{ margin: "10px 0" }}>
          {error}
        </div>
      )}

      <div
        ref={mapContainerRef}
        style={{
          width: "100%",
          height: "calc(100vh - 180px)",
          minHeight: "450px",
          borderRadius: "8px",
          border: "1px solid var(--border, #e2e8f0)",
          zIndex: 1,
        }}
      />
    </div>
  );
}
